import { fetchWithAuth, type TokenGetter } from "src/infrastructure/api/http";
import type { PortalSession, StartedSession } from "./session.types";

/* ---- Wire shapes (customer-portal-api, camelCase JSON). Documentation and
   fixtures only: the mappers take `unknown` and validate every field. ---- */

export interface RawPortalSession {
  userId: string;
  roles: string[] | null;
  partnerOrganizationId: string | null;
  hasPartnerOrganization: boolean;
  /** Returned by the start (POST) only. */
  sessionId?: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.length > 0;
}

/** A list of strings; `null` (the API's "none") becomes `[]`. */
function toStringList(value: unknown): string[] | undefined {
  if (value === null) return [];
  return Array.isArray(value) && value.every((item) => typeof item === "string")
    ? value
    : undefined;
}

/**
 * Validates an untrusted payload and throws if any field is malformed, as
 * toMetricsPortfolio does for an unknown tier. `hasPartnerOrganization` must
 * agree with `partnerOrganizationId`.
 */
export function toPortalSession(raw: unknown): PortalSession {
  const malformed = new Error("Malformed portal session from customer-portal-api.");
  if (!isRecord(raw) || !isNonEmptyString(raw.userId)) throw malformed;

  const roles = toStringList(raw.roles);
  const orgId = raw.partnerOrganizationId;
  const orgValid = orgId === null || isNonEmptyString(orgId);
  const flagAgrees =
    typeof raw.hasPartnerOrganization === "boolean" &&
    raw.hasPartnerOrganization === (orgId !== null);
  if (!roles || !orgValid || !flagAgrees) throw malformed;

  return { userId: raw.userId, roles, partnerOrganizationId: orgId };
}

export function toEntitlements(raw: unknown): string[] {
  const entitlements = isRecord(raw) ? toStringList(raw.entitlements) : undefined;
  if (!entitlements) throw new Error("Malformed entitlements from customer-portal-api.");
  return entitlements;
}

/** The session id a start returns; required, since every later call sends it. */
export function toSessionId(raw: unknown): string {
  const id = isRecord(raw) ? raw.sessionId : undefined;
  if (!isNonEmptyString(id)) throw new Error("Portal session start returned no sessionId.");
  return id;
}

/** POSTs to start a session, which records "portal session started" and returns its id. */
export async function startPortalSession(getToken: TokenGetter): Promise<StartedSession> {
  const raw = await fetchWithAuth<unknown>("/api/portal/session", { method: "POST" }, getToken, {
    withoutSession: true,
  });
  return { session: toPortalSession(raw), sessionId: toSessionId(raw) };
}

/** GETs the current session's state without recording anything. */
export async function fetchPortalSession(getToken: TokenGetter): Promise<PortalSession> {
  const raw = await fetchWithAuth<unknown>("/api/portal/session", { method: "GET" }, getToken);
  return toPortalSession(raw);
}

export async function fetchMyEntitlements(getToken: TokenGetter): Promise<string[]> {
  const raw = await fetchWithAuth<unknown>("/api/user/entitlements", { method: "GET" }, getToken);
  return toEntitlements(raw);
}
